import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Stethoscope, ShieldCheck, IdCard, PencilLine } from 'lucide-react'
import { useActivePatient } from '@context/ActivePatientContext'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { useAuthStore } from '@hooks/useAuthStore'
import { recordAccessService } from '@services/recordAccess.service'
import { ActiveConsultationView } from '@components/consultation/ActiveConsultationView'

/**
 * Active-consultation screen for the clinical record (NOM-004 §6.2).
 *
 * A single screen with a header strip and a body split into two side-by-side
 * zones: Zone A (left, read-only clinical context) and Zone B (right, the new
 * progress note — the only editable area).
 *
 *  - REQ-VIS-04: the two zones are unambiguously differentiated (background +
 *    header) so it is always clear what is context vs. what is being edited.
 *  - REQ-VIS-06: while unsigned, the draft does not appear in the evolution
 *    timeline nor mixed with signed notes (Zone A reads signed notes only); it
 *    joins the timeline only on signing.
 *  - REQ-VIS-07: on narrow/stacked layouts the zone headers and background
 *    contrast are retained (each zone owns its own header + surface color).
 *  - REQ-NOTE-03 / REQ-INT-01: delegated to ConsultationForm inside Zone B.
 */
export function ActiveConsultationScreen() {
  const { activePatientData } = useActivePatient()
  const { formatDateTime } = useClinicFormat()
  const user = useAuthStore((s) => s.user)
  // "Continuar" on a draft (Evolución finder) deep-links here with the draft's
  // appointment so the editor opens that exact draft.
  const [searchParams] = useSearchParams()
  const initialAppointmentId = searchParams.get('appointment') ?? 'none'

  const { data: relationship } = useQuery({
    queryKey: ['record-relationship', activePatientData?.id],
    queryFn: () => recordAccessService.getRelationship(activePatientData!.id),
    enabled: !!activePatientData?.id,
  })

  // The encounter timestamp for an in-progress consultation is "now". Computed
  // once per mount so it does not jitter on re-render.
  const encounterStamp = useMemo(() => formatDateTime(new Date().toISOString()), [formatDateTime])

  if (!activePatientData) return null

  const signerName = user ? `${user.first_name} ${user.last_name}` : '—'

  return (
    <div className="animate-fade-in space-y-6">
      {/* ── Header strip ─────────────────────────────────────────────── */}
      <header className="rounded-2xl border border-ml-outline-variant/10 bg-ml-surface-container-lowest px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Patient name + record ID */}
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ml-primary/10 text-ml-primary">
              <Stethoscope className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-headline italic text-ml-primary leading-tight truncate">
                {activePatientData.full_name}
              </h2>
              <p className="text-[11px] font-mono text-ml-on-surface-variant/70">
                Exp. {activePatientData.mrn}
              </p>
            </div>
          </div>

          {/* Encounter meta: date/time, signer + relationship, access logged */}
          <div className="flex flex-col items-start sm:items-end gap-1 text-xs">
            <span className="text-ml-on-surface-variant">
              Encuentro: <span className="font-medium text-ml-on-surface">{encounterStamp}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-ml-on-surface-variant">
              <IdCard className="h-3.5 w-3.5 text-ml-on-surface-variant/70" />
              <span className="font-medium text-ml-on-surface">{signerName}</span>
              {user?.cedula_profesional && (
                <span className="text-ml-on-surface-variant/70">· Céd. {user.cedula_profesional}</span>
              )}
              <span className="text-ml-on-surface-variant/70">
                · {relationship?.relationship ?? 'médico tratante'}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-teal-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Acceso registrado en la bitácora</span>
            </span>
          </div>
        </div>
      </header>

      {/* ── Body: the new progress note (the only editable area) ──────── */}
      <div>
        {/* Zone B — new progress note, the only editable area */}
        <section className="rounded-2xl border border-ml-primary/15 bg-ml-surface-container-lowest p-5">
          <div className="flex flex-wrap items-center gap-2 pb-3 mb-4 border-b border-ml-outline-variant/15">
            <PencilLine className="h-4 w-4 text-ml-primary" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-ml-primary">
              Nueva nota de evolución
            </h3>
            <span className="inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
              Borrador · sin firmar
            </span>
          </div>

          {/* Author + license are attributable to the signed-in clinician and are
              not editable (the signature is auto-filled on signing). */}
          <p className="mb-4 text-[11px] text-ml-on-surface-variant/70">
            Autor: <span className="font-medium text-ml-on-surface">{signerName}</span>
            {user?.cedula_profesional && <> · Cédula profesional {user.cedula_profesional}</>}
          </p>

          <ActiveConsultationView embedded initialAppointmentId={initialAppointmentId} />
        </section>
      </div>
    </div>
  )
}
